/**
 * File operation utilities for chat components
 * Pure functions for file handling, validation, and preparation
 */

import type { UploadedFile, FileUploadStatus } from "@/stores/ChatStore";

/**
 * File extensions accepted by every chat composer's attach control.
 *
 * Shared deliberately. This list was previously duplicated between
 * `PromptWithActions.tsx` (the main chat) and `BuildChatComposer.tsx` (the
 * Workflow Builder's build chat), and the two copies had already drifted, with
 * `.xml` present in one and absent from the other, so the same file was
 * attachable in one composer and rejected by the other for no stated reason.
 * Any new composer must import this rather than restate it.
 */
export const ALLOWED_FILE_TYPES = [
  ".pdf",
  ".doc",
  ".docx",
  ".pptx",
  ".png",
  ".jpg",
  ".jpeg",
  ".svg",
  ".csv",
  ".xlsx",
  ".xls",
  ".txt",
  ".md",
  ".json",
  ".yaml",
  ".yml",
  ".xml",
];

/**
 * Interval between polls while waiting for an upload to finish processing,
 * in milliseconds. Shared by every composer for the same reason as
 * `ALLOWED_FILE_TYPES`.
 */
export const UPLOAD_POLL_INTERVAL_MS = 1000;

/**
 * Translates a stable upload status key into a localised display label.
 * Status is stored as a key (not a translated string) so logic comparisons
 * stay locale-independent; this is the single place it becomes user-facing.
 *
 * @param status - Stable status key.
 * @param t - Translation function.
 * @returns Localised label for display.
 */
export const fileUploadStatusLabel = (
  status: FileUploadStatus,
  t: (key: string) => string,
): string => {
  const labels: Record<FileUploadStatus, string> = {
    uploading: t("Uploading"),
    processing: t("Processing"),
    completed: t("Completed"),
  };

  return labels[status];
};

/**
 * Creates a DataTransfer object with the provided files
 * Used for programmatically adding files to file inputs
 *
 * @param files - Array of File objects to add to the DataTransfer
 * @returns DataTransfer object containing the files
 */
export const createDataTransfer = (files: File[]): DataTransfer => {
  const dataTransfer = new DataTransfer();

  files.forEach((file) => dataTransfer.items.add(file));

  return dataTransfer;
};

/**
 * Prepares files for upload by creating UploadedFile objects with temporary IDs
 * These temporary files are shown in the UI immediately while the actual upload happens
 *
 * @param files - Array of File objects to prepare for upload
 * @param status - Stable status key to set (e.g., "uploading")
 * @param timestamp - Optional timestamp for ID generation (defaults to current time)
 * @returns Array of UploadedFile objects with temporary IDs
 */
export const prepareFilesForUpload = (
  files: File[],
  status: FileUploadStatus,
  timestamp?: number,
): UploadedFile[] => {
  const ts = timestamp ?? Date.now();

  return files.map((file, index) => ({
    name: file.name,
    size: String(file.size),
    type: file.type,
    id: `uploading-${file.name}-${ts}-${index}`,
    status,
  }));
};

/**
 * Creates a synthetic file input change event for retry functionality
 * Simulates a user selecting files through a file input
 *
 * @param files - DataTransfer object containing files
 * @returns Synthetic ChangeEvent that mimics a file input change
 */
export const createSyntheticFileEvent = (
  files: DataTransfer,
): React.ChangeEvent<HTMLInputElement> => {
  return {
    target: {
      files: files.files,
      value: "",
    },
    preventDefault: () => {},
    stopPropagation: () => {},
  } as React.ChangeEvent<HTMLInputElement>;
};

/**
 * Handles retrying failed file uploads by creating a synthetic event
 * Used to programmatically trigger file upload retry without user interaction
 *
 * @param failedFiles - Array of File objects that failed to upload
 * @param fileInputRef - Reference to the hidden file input element
 * @param onFileChange - Handler function for file changes
 */
export const handleFailedFileRetry = (
  failedFiles: File[],
  fileInputRef: React.RefObject<HTMLInputElement>,
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
): void => {
  if (failedFiles.length === 0 || !fileInputRef.current) {
    return;
  }

  const dataTransfer = createDataTransfer(failedFiles);

  // In real browser, we'd set: fileInputRef.current.files = dataTransfer.files;
  // But for testing compatibility, we pass the files directly to the handler
  const syntheticEvent = createSyntheticFileEvent(dataTransfer);

  onFileChange(syntheticEvent);
};

/**
 * Updates the status of a specific file in the upload list
 * Used to update file status from "Uploading" to "Processing" or "Complete"
 *
 * @param files - Array of UploadedFile objects
 * @param fileId - ID of the file to update
 * @param newStatus - New stable status key to set
 * @returns Updated array with the file's status changed
 */
export const updateFileStatus = (
  files: UploadedFile[],
  fileId: string,
  newStatus: FileUploadStatus,
): UploadedFile[] => {
  return files.map((f) => (f.id === fileId ? { ...f, status: newStatus } : f));
};

/**
 * Removes a file from the upload list by name
 * Used when user deletes a file or upload fails
 *
 * @param files - Array of UploadedFile objects
 * @param fileName - Name of the file to remove
 * @returns Array with the specified file removed
 */
export const removeFileByName = (files: UploadedFile[], fileName: string): UploadedFile[] => {
  return files.filter((file) => file.name !== fileName);
};

/**
 * Checks if any files are currently being uploaded or processed
 * Used to disable UI elements (send button, delete buttons) during upload
 *
 * @param files - Array of UploadedFile objects to check
 * @returns True if any files are in progress, false otherwise
 */
export const hasFilesInProgress = (files: UploadedFile[]): boolean => {
  return files.some((f) => f.status === "uploading" || f.status === "processing");
};

/**
 * Calculates incremental upload progress with a maximum cap
 * Prevents progress from reaching 100% before upload actually completes
 *
 * @param currentProgress - Current progress value (0-100)
 * @param increment - Amount to increment by
 * @param maxProgress - Maximum allowed progress (default: 95)
 * @returns New progress value, capped at maxProgress
 */
export const calculateUploadProgress = (
  currentProgress: number,
  increment: number,
  maxProgress: number = 95,
): number => {
  const newProgress = currentProgress + increment;

  return Math.min(newProgress, maxProgress);
};

/**
 * True while an attachment has no stored id the backend can read.
 *
 * Covers the whole window in which the file still carries the temporary
 * `uploading-` id from `prepareFilesForUpload`: while it uploads, while it is
 * processed, and during the short "completed" display before the stored id
 * replaces the temporary one. Sending in that window used to pass the
 * temporary id to the backend, which could not find the file (PWI-400).
 */
export const isPendingUpload = (file: UploadedFile): boolean =>
  Boolean(file.id?.startsWith("uploading-")) ||
  file.status === "uploading" ||
  file.status === "processing";

/** True when any attachment is still pending; sending must wait for it. */
export const hasPendingUploads = (files: UploadedFile[]): boolean => files.some(isPendingUpload);

/**
 * The attachments that may go into a chat payload: those with a stored id.
 * A pending upload or a file without an id is never sent.
 */
export const getSendableFiles = (files: UploadedFile[]): UploadedFile[] =>
  files.filter((f) => Boolean(f.id) && !isPendingUpload(f));
